import {zodResolver} from '@hookform/resolvers/zod';
import type {ConfirmSignInInput} from '@aws-amplify/auth';
import {PasswordInput, rem} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Progress} from 'src/design/atoms/Progress';
import {Text} from 'src/design/atoms/Text';
import {IconCheck, IconX} from '@tabler/icons-react';
import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {SubmitButton} from 'src/design/molecules/SubmitButton';
import {useAppDispatch} from 'src/redux/hooks';
import {confirmSignInUser} from 'src/redux/slices/authSlice';
import {z} from 'zod';

const schema = z
  .object({
    password: z.string().min(8),
    confirmPassword: z.string().min(8),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'], // path of error
  })
  .superRefine(({password}, checkPassComplexity) => {
    const containsUppercase = (ch: string) => /[A-Z]/.test(ch);
    const containsLowercase = (ch: string) => /[a-z]/.test(ch);
    const containsSpecialChar = (ch: string) =>
      /[`!@#$%^&*()_\-+=[\]{};':"\\|,.<>/?~ ]/.test(ch);
    let countOfUpperCase = 0,
      countOfLowerCase = 0,
      countOfNumbers = 0,
      countOfSpecialChar = 0;
    for (let i = 0; i < password.length; i++) {
      const ch = password.charAt(i);
      if (!isNaN(+ch)) countOfNumbers++;
      else if (containsUppercase(ch)) countOfUpperCase++;
      else if (containsLowercase(ch)) countOfLowerCase++;
      else if (containsSpecialChar(ch)) countOfSpecialChar++;
    }
    if (
      countOfLowerCase < 1 ||
      countOfUpperCase < 1 ||
      countOfSpecialChar < 1 ||
      countOfNumbers < 1
    ) {
      checkPassComplexity.addIssue({
        code: 'custom',
        message: 'password does not meet complexity requirements',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

function PasswordRequirement({meets, label}: {meets: boolean; label: string}) {
  return (
    <Text
      c={meets ? 'teal' : 'red'}
      style={{display: 'flex', alignItems: 'center'}}
      mt={7}
      size="sm">
      {meets ? (
        <IconCheck style={{width: rem(14), height: rem(14)}} />
      ) : (
        <IconX style={{width: rem(14), height: rem(14)}} />
      )}{' '}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

const requirements = [
  {re: /[0-9]/, label: 'Includes number'},
  {re: /[a-z]/, label: 'Includes lowercase letter'},
  {re: /[A-Z]/, label: 'Includes uppercase letter'},
  {re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Includes special symbol'},
];

function getStrength(password: string) {
  let multiplier = password.length > 5 ? 0 : 1;

  requirements.forEach(requirement => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

export const ConfirmSignInWithNewPasswordRequired: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(schema),
  });

  const password = watch('password');

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(password)}
    />
  ));

  const strength = getStrength(password);
  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

  const handleSubmit = async (values: FormValues) => {
    const input = {
      challengeResponse: values.confirmPassword,
    } as ConfirmSignInInput;

    void dispatch(confirmSignInUser(input));
  };

  return (
    <>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Text mb="md">
          You must change your password before logging in. This may be due to an
          administrator or automated security action. Please enter your new
          password below.
        </Text>

        <Controller
          name="password"
          control={control}
          render={({field, fieldState}) => (
            <PasswordInput
              label="New Password"
              placeholder="New Password"
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({field, fieldState}) => (
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm New Password"
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />

        <Progress color={color} value={strength} size={5} mb="xs" mt="xs" />
        <PasswordRequirement
          label="Includes at least 6 characters"
          meets={password.length > 5}
        />
        {checks}

        <SubmitButton mt="lg">Confirm</SubmitButton>
      </form>
    </>
  );
};
